import { createContext, lazy, Suspense, useContext, useEffect, useRef, useState, type ReactNode, type RefObject } from 'react'
import { AlertCircle, CheckCircle2, ChevronRight, Loader2 } from 'lucide-react'
import { Modal } from '@/components/ui'
import { usePageHeader } from '@/components/layout/pageHeader'

const PdfPreview = lazy(() => import('@/components/ui/PdfPreview'))

export const ndtInputCls =
  'w-full px-3 py-2 text-sm border border-slate-300 rounded-md ' +
  'focus:outline-none focus:border-[#1E3A5F] bg-white text-slate-800 transition-colors min-h-[44px] ' +
  'placeholder:text-slate-400 disabled:opacity-40 disabled:bg-slate-50 disabled:cursor-not-allowed'

export const ndtSelectCls = `${ndtInputCls} appearance-none`

export const ndtTextareaCls =
  'w-full px-3 py-2 text-sm border border-slate-300 rounded-md ' +
  'focus:outline-none focus:border-[#1E3A5F] bg-white text-slate-800 transition-colors min-h-[88px] ' +
  'placeholder:text-slate-400 resize-y'

export const ndtLabelCls =
  'block text-[10px] font-semibold uppercase tracking-widest text-slate-400 mb-1'

export const ndtHintCls = 'text-xs text-slate-400 mb-1'

export const ndtToggleCls = (selected: boolean) =>
  `flex-1 px-3 min-h-[44px] border rounded-md text-sm font-semibold transition-colors ${
    selected
      ? 'border-[#1E3A5F] bg-[#1E3A5F] text-white'
      : 'border-slate-200 text-slate-700 hover:bg-slate-50'
  }`

export const ndtUploadCls =
  'flex items-center justify-center gap-2 w-full px-3 py-3 border border-dashed border-slate-300 rounded-md cursor-pointer hover:border-[#1E3A5F] hover:bg-slate-50 transition-colors bg-white min-h-[44px]'

export const ndtSecondaryBtnCls =
  'w-full h-11 px-4 text-sm font-semibold text-[#1E3A5F] bg-white border border-[#1E3A5F] rounded-md hover:bg-slate-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed'

export const ndtPrimaryBtnCls =
  'w-full h-11 px-4 text-sm font-semibold text-white bg-[#1E3A5F] rounded-md hover:bg-[#152C4A] transition-colors disabled:opacity-50 disabled:cursor-not-allowed'

export function NdtStatusButtons({
  value,
  onChange,
}: {
  value: 'Accepted' | 'Not Accepted'
  onChange: (value: 'Accepted' | 'Not Accepted') => void
}) {
  return (
    <div>
      <label className={ndtLabelCls}>Status</label>
      <div className="grid grid-cols-2 gap-3">
        <button
          type="button"
          onClick={() => onChange('Accepted')}
          className={`min-h-[44px] rounded-md border text-sm font-semibold transition-colors ${
            value === 'Accepted'
              ? 'bg-emerald-100 text-emerald-800 border-emerald-400'
              : 'bg-white text-slate-500 border-slate-300 hover:bg-slate-50'
          }`}
        >
          Godkjent
        </button>
        <button
          type="button"
          onClick={() => onChange('Not Accepted')}
          className={`min-h-[44px] rounded-md border text-sm font-semibold transition-colors ${
            value === 'Not Accepted'
              ? 'bg-red-100 text-red-800 border-red-400'
              : 'bg-white text-slate-500 border-slate-300 hover:bg-slate-50'
          }`}
        >
          Underkjent
        </button>
      </div>
    </div>
  )
}

type RequiredControl = HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement

interface RequiredField {
  el: RequiredControl
  label: string
  filled: boolean
}

const RequiredFieldsContext = createContext<RequiredField[]>([])

function labelFor(el: RequiredControl): string {
  let node = el.parentElement
  for (let depth = 0; depth < 4 && node; depth++) {
    const label = node.querySelector(':scope > label')
    const text = (label?.firstChild?.textContent || label?.textContent || '').replace(/\*/g, '').trim()
    if (text) return text
    node = node.parentElement
  }
  return el.getAttribute('aria-label') || el.getAttribute('placeholder') || 'Felt'
}

function isFilled(el: RequiredControl, root: HTMLElement): boolean {
  if (el instanceof HTMLInputElement && el.type === 'checkbox') return el.checked
  if (el instanceof HTMLInputElement && el.type === 'radio') {
    if (!el.name) return el.checked
    return !!root.querySelector(`input[type="radio"][name="${CSS.escape(el.name)}"]:checked`)
  }
  return el.value.trim() !== ''
}

function sameFields(a: RequiredField[], b: RequiredField[]) {
  return a.length === b.length && a.every((f, i) => f.el === b[i].el && f.label === b[i].label && f.filled === b[i].filled)
}

/** Reads the form's own `required` attributes, so it follows each form without extra config. */
function useRequiredFields(rootRef: RefObject<HTMLElement | null>) {
  const [fields, setFields] = useState<RequiredField[]>([])

  useEffect(() => {
    const root = rootRef.current
    if (!root) return
    const seenRadioNames = new Set<string>()
    const next: RequiredField[] = []
    root.querySelectorAll<RequiredControl>('input[required], select[required], textarea[required]').forEach((el) => {
      if (el.disabled || el.getClientRects().length === 0) return
      if (el instanceof HTMLInputElement && el.type === 'radio' && el.name) {
        if (seenRadioNames.has(el.name)) return
        seenRadioNames.add(el.name)
      }
      next.push({ el, label: labelFor(el), filled: isFilled(el, root) })
    })
    setFields((prev) => (sameFields(prev, next) ? prev : next))
  })

  return fields
}

function focusField(el: RequiredControl) {
  el.scrollIntoView({ block: 'center', behavior: 'smooth' })
  el.focus({ preventScroll: true })
}

function NdtFormSidePanel({ highlight, title }: { highlight: string; title: string }) {
  const fields = useContext(RequiredFieldsContext)
  const missing = fields.filter((f) => !f.filled)
  const done = fields.length - missing.length

  return (
    <aside className="hidden lg:block sticky top-5 self-start space-y-4">
      <div className="bg-white border border-slate-200 rounded-xl p-5">
        <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">Rapport</p>
        <p className="mt-1 text-lg font-semibold text-slate-900 leading-snug">
          <span className="text-[#0D9488]">{highlight}</span> {title}
        </p>
      </div>

      {fields.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-xl p-5">
          <div className="flex items-baseline justify-between gap-3">
            <p className="text-sm font-semibold text-slate-800">Påkrevde felt</p>
            <p className="text-sm font-semibold tabular-nums text-slate-600">
              {done}/{fields.length}
            </p>
          </div>
          <div className="mt-3 h-2 bg-slate-100 rounded-full overflow-hidden">
            <div
              className={`h-full rounded-full transition-all duration-300 ${missing.length === 0 ? 'bg-emerald-600' : 'bg-[#1E3A5F]'}`}
              style={{ width: `${(done / fields.length) * 100}%` }}
            />
          </div>

          {missing.length === 0 ? (
            <p className="mt-4 flex items-center gap-2 text-sm font-medium text-emerald-700">
              <CheckCircle2 size={18} className="shrink-0" />
              Alle påkrevde felt er utfylt
            </p>
          ) : (
            <ul className="mt-3 -mx-2">
              {missing.map((f, i) => (
                <li key={i}>
                  <button
                    type="button"
                    onClick={() => focusField(f.el)}
                    className="w-full flex items-center gap-2 px-2 min-h-[48px] rounded-md text-left text-sm font-medium text-slate-700 hover:bg-slate-50 active:bg-slate-100 transition-colors"
                  >
                    <AlertCircle size={16} className="shrink-0 text-amber-600" />
                    <span className="flex-1 min-w-0 truncate">{f.label}</span>
                    <ChevronRight size={16} className="shrink-0 text-slate-400" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </aside>
  )
}

/** Sticky bottom bar for a form's preview/submit buttons. Keep it as the last child inside the `<form>`. */
export function NdtFormActions({ children }: { children: ReactNode }) {
  const fields = useContext(RequiredFieldsContext)
  const missing = fields.filter((f) => !f.filled)

  return (
    <div
      className="sticky bottom-0 z-20 -mx-3 px-3 pt-2 lg:mx-0 lg:px-0"
      style={{ paddingBottom: 'calc(0.75rem + env(safe-area-inset-bottom))' }}
    >
      <div className="bg-white/95 backdrop-blur-sm border border-slate-200 rounded-xl p-3 shadow-[0_-4px_20px_rgba(15,23,42,0.08)]">
        {missing.length > 0 && (
          <button
            type="button"
            onClick={() => focusField(missing[0].el)}
            className="lg:hidden w-full flex items-center gap-2 mb-2 px-1 min-h-[40px] text-left text-sm font-medium text-amber-700"
          >
            <AlertCircle size={16} className="shrink-0" />
            <span className="flex-1 min-w-0 truncate">
              {missing.length === 1 ? `Mangler: ${missing[0].label}` : `${missing.length} påkrevde felt mangler – første: ${missing[0].label}`}
            </span>
            <ChevronRight size={16} className="shrink-0" />
          </button>
        )}
        <div className="grid grid-cols-2 gap-3 [&>button]:h-12 [&>button]:text-base">
          {children}
        </div>
      </div>
    </div>
  )
}

interface NdtFormPageProps {
  title: string
  highlight: string
  children: ReactNode
  loading?: boolean
  previewUrl?: string | null
  previewTitle?: string
  onClosePreview?: () => void
  onPublish?: () => void
  isPublishing?: boolean
  canPublish?: boolean
  maxWidthClass?: string
}

const NdtFormPage = ({
  title,
  highlight,
  children,
  loading = false,
  previewUrl,
  previewTitle = 'PDF',
  onClosePreview,
  onPublish,
  isPublishing = false,
  canPublish = true,
  maxWidthClass = 'max-w-2xl',
}: NdtFormPageProps) => {
  usePageHeader(`${highlight} ${title}`, { hideAppHeader: !!previewUrl })
  const formAreaRef = useRef<HTMLDivElement>(null)
  const requiredFields = useRequiredFields(formAreaRef)

  return (
    <div className="flex-1 min-h-0 overflow-y-auto overscroll-none" style={{ backgroundColor: 'var(--bg-primary)' }}>
      <div className="max-w-6xl mx-auto px-3 pt-3 lg:px-6 lg:pt-5">
        {loading ? (
          <div className="flex items-center justify-center py-24 text-slate-500">
            <Loader2 className="w-8 h-8 animate-spin mr-3" />
            <span className="text-sm">Laster skjema…</span>
          </div>
        ) : (
          <RequiredFieldsContext.Provider value={requiredFields}>
            <div className="lg:grid lg:grid-cols-[minmax(0,1fr)_18rem] xl:grid-cols-[minmax(0,1fr)_20rem] lg:gap-6">
              <div ref={formAreaRef} className={`space-y-4 w-full ${maxWidthClass} mx-auto`}>
                {children}
              </div>
              <NdtFormSidePanel highlight={highlight} title={title} />
            </div>
          </RequiredFieldsContext.Provider>
        )}
      </div>

      <Modal
        isOpen={!!previewUrl}
        onClose={onClosePreview ?? (() => undefined)}
        title={previewTitle}
        size="xl"
        actions={onPublish ? (
          <button
            type="button"
            onClick={onPublish}
            disabled={isPublishing || !canPublish}
            className="px-5 h-11 bg-[#1E3A5F] hover:bg-[#152C4A] text-white font-semibold text-sm rounded-md disabled:opacity-50"
          >
            {isPublishing ? 'Publiserer…' : 'Publiser'}
          </button>
        ) : undefined}
      >
        {previewUrl && (
          <div className="min-h-[60vh]">
            <Suspense
              fallback={
                <div className="flex items-center justify-center py-16 text-slate-400">
                  <Loader2 className="w-6 h-6 animate-spin mr-2" />
                  Laster forhåndsvisning…
                </div>
              }
            >
              <PdfPreview src={previewUrl} title={previewTitle} />
            </Suspense>
          </div>
        )}
      </Modal>
    </div>
  )
}

export default NdtFormPage
